import { useState, useEffect, useMemo, useCallback } from 'react';
import { useUserPaymentCanisterContext } from './userPaymentCanisterProvider';

/**
 * Hook to fetch and manage modal configuration from the user payment canister
 */
export const useModalConfig = (modalId = 'modal_1') => {
  const { userPaymentService } = useUserPaymentCanisterContext();
  const [modalConfig, setModalConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);



  const fetchModalConfig = useCallback(async () => {
    if (!userPaymentService) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      let modals = await userPaymentService.actor.list_my_modals()
      let modalId;
      if(modals && modals[0]){
       modalId= modals[0].modal_id
      }

      const config = await userPaymentService.actor.get_modal_config(modalId);
      setModalConfig(config.Ok);
    } catch (err) {
      setError(err.message);
      // Use default configuration on error
      setModalConfig(getDefaultModalConfig());
    } finally {
      setLoading(false);
    }
  }, [userPaymentService]);

  useEffect(() => {
    fetchModalConfig();
  }, [fetchModalConfig]);

  useEffect(() => {
    if (modalConfig?.theme) {
      setModalConfig(prev => ({
        ...prev,
        theme: {
          primary_color: modalConfig.theme.primary_color,
          background_color: modalConfig.theme.background_color,
          text_color: modalConfig.theme.text_color,
          border_radius: Number(modalConfig.theme.border_radius || 8),
          font_family: modalConfig.theme.font_family
        }
      }));
    }
  }, [modalConfig?.theme]);

  // Generate dynamic CSS styles based on modal config
  const dynamicStyles = useMemo(() => {
    if (!modalConfig?.theme) {
      return {};
    }

    // Map theme values with fallbacks
    return {
      '--primary-color': modalConfig.theme.primary_color || '#3b82f6',
      '--background-color': modalConfig.theme.background_color || '#FFFFFF',
      '--text-color': modalConfig.theme.text_color || '#1F2937',
      '--border-radius': `${modalConfig.theme.border_radius || 8}px`,
      '--font-family': modalConfig.theme.font_family || 'inherit'
    };
  }, [modalConfig]);

  // Apply styles to modal container
  const applyDynamicStyles = (element) => {
    if (!element || !modalConfig?.theme) return;

    Object.entries(dynamicStyles).forEach(([property, value]) => {
      element.style.setProperty(property, value);
    });
  };

  return {
    modalConfig,
    loading,
    error,
    dynamicStyles,
    applyDynamicStyles,
    theme: modalConfig?.theme ? {
      primary_color: modalConfig.theme.primary_color || '#3b82f6',
      background_color: modalConfig.theme.background_color || '#FFFFFF',
      text_color: modalConfig.theme.text_color || '#1F2937',
      border_radius: Number(modalConfig.theme.border_radius || 8),
      font_family: modalConfig.theme.font_family || 'inherit'
    } : getDefaultModalConfig().theme,
    branding: modalConfig?.branding || getDefaultModalConfig().branding,
    paymentOptions: modalConfig?.payment_options || getDefaultModalConfig().payment_options,
    refreshConfig: () => fetchModalConfig()
  };
};

/**
 * Default modal configuration fallback
 */
const getDefaultModalConfig = () => ({
  modal_id: 'default',
  name: 'Payment Modal',
  description: 'Default payment modal configuration',
  theme: null,
  payment_options: {
    allowed_tokens: [],
    require_email: false,
    require_shipping: false,
    show_amount_breakdown: true,
    enable_tips: false
  },
  branding: {
    logo_url: null,
    company_name: 'Your Company',
    support_url: null,
    terms_url: null
  },
  redirect_urls: {
    success_url: '',
    cancel_url: '',
    webhook_url: null
  },
  template_id: null,
  is_active: true
});

export default useModalConfig;
